import type {
  CheckoutBookingForm,
  CheckoutPrefill,
  CheckoutTravelerEdits,
} from './checkout_source.js';
import type { AllocationDraft } from '../fields/allocation.js';

type PrefillAllocation = NonNullable<
  NonNullable<CheckoutPrefill['travelers']>[number]['allocation']
>;

/** Valida os ids do prefill contra o form FRESCO. Empresa/CC ausentes ⇒ null
 *  (cai no seed padrão; completude falha; drawer pede o campo). Projeto stale é
 *  dropado sozinho — enableProjectField decide se ele volta a ser exigido. */
export function allocationFromPrefill(
  form: CheckoutBookingForm,
  prefill: PrefillAllocation | undefined,
): AllocationDraft | null {
  if (!prefill) return null;
  const company = form.companies.find((c) => c.id === prefill.companyId);
  if (!company) return null;
  if (!company.costCenters.some((cc) => cc.id === prefill.costCenterId)) return null;
  const projectId =
    prefill.projectId && company.projects.some((p) => p.id === prefill.projectId)
      ? prefill.projectId
      : '';
  return { companyId: prefill.companyId, costCenterId: prefill.costCenterId, projectId };
}

/** O que gateia cada chave de `edits`: ou um dos `missingRequired` que o
 *  servidor devolve, ou `'phone'` — o telefone não entra em `missingRequired`
 *  (quem o exige é o MANIFESTO, no voo/mobility), então o gate dele é a
 *  presença do número em ficha. `Record` completo de propósito: uma chave nova
 *  em `CheckoutTravelerEdits` sem entrada aqui não compila. */
const EDIT_GATE = {
  birthdate: 'birthdate',
  nationality: 'nationality',
  document: 'document',
  gender: 'gender',
  boardingName: 'embark',
  boardingSurname: 'embark',
  phone: 'phone',
  countryCode: 'phone',
} as const satisfies Record<
  keyof CheckoutTravelerEdits,
  NonNullable<NonNullable<CheckoutBookingForm['traveler']>['missingRequired']>[number] | 'phone'
>;

/** Filtra os `edits` do prefill contra o PERFIL FRESCO: um dado que o perfil já
 *  satisfaz não pode ser sobrescrito pelo da compra anterior.
 *
 *  O caso que motiva: passaporte renovado desde a última compra. O perfil traz o
 *  novo (o servidor nem lista `document` em `missingRequired`), e o prefill
 *  carrega o VELHO — sem este filtro o card confirmaria, calado, uma reserva com
 *  o documento vencido. Mesma história com o telefone.
 *
 *  A regra é uma só: o prefill preenche BURACO, nunca sobrescreve. Um campo que
 *  o servidor flagou como faltante (ou um telefone que não está em ficha) segue
 *  vindo do prefill — é exatamente aí que ele poupa digitação. */
export function filterPrefillEdits(
  edits: CheckoutTravelerEdits | undefined,
  form: CheckoutBookingForm,
): CheckoutTravelerEdits {
  if (!edits) return {};
  const missing = new Set(form.traveler?.missingRequired ?? []);
  const phoneOnFile = Boolean(form.traveler?.phone);
  const isHole = (key: keyof CheckoutTravelerEdits): boolean => {
    const gate = EDIT_GATE[key];
    return gate === 'phone' ? !phoneOnFile : missing.has(gate);
  };
  const kept = Object.entries(edits).filter(([key]) => isHole(key as keyof CheckoutTravelerEdits));
  return Object.fromEntries(kept);
}
